import { Button, Input } from 'antd';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useChangePassword } from './useChangePassword';

import Popup from '../../components/Popup/Popup';

import { Props } from './ChangePassword.model';

import styles from './ChangePassword.module.scss';

const ChangePassword = ({ onClose }: Props) => {
  const {
    control,
    errors,
    handleSubmit,
    onPasswordChange,
    onSubmitPasswordForm,
    state: { passwordStrength },
    watchPassword
  } = useChangePassword({ onClose });

  const intl = useIntl();

  const passwordPopupActions = (<div>
    <Button type="default" size="large" onClick={onClose}>
      <FormattedMessage id="buttons.cancel" />
    </Button>
    <Button type="primary" size="large" onClick={handleSubmit(onSubmitPasswordForm)}>
      <FormattedMessage id="buttons.save" />
    </Button>
  </div>);

  const renderPasswordPopup = () => {
    return <Popup
      actions={passwordPopupActions}
      onClose={onClose}
      title={{ action: '', target: 'password' }}
    >
      <div className={styles.popupContent}>
        <div className={styles.category}>
          <h2><FormattedMessage id="userSettings.changePassword" /></h2>
          <div className={styles.categoryContent}>
            <div className={styles.fieldWrapper}>
              <label className={styles.floatingLabel}>
                {<FormattedMessage id={`userSettings.oldPassword`} />}
              </label>
              <Controller
                name='oldPassword'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    placeholder={intl.formatMessage({ id: 'formPlaceholders.oldPassword' })}
                    visibilityToggle={false}
                  />
                )}
              />
            </div>
            <div className={styles.fieldWrapper}>
              <label className={styles.floatingLabel}>
                {<FormattedMessage id={`userSettings.newPassword`} />}
              </label>
              <Controller
                name='newPassword'
                control={control}
                rules={{
                  min: 8,
                  pattern: /^(?=.*[a-z])(?=.*[0-9]).{8,}$/,
                  required: intl.formatMessage({ id: 'formErrors.password.required' }),
                }}
                render={({ field }) => (
                  <div className={styles.passwordWrapper}>
                    <Input.Password
                      {...field}
                      onChange={(event) => {
                        field.onChange(event);
                        onPasswordChange(event);
                      }}
                      placeholder={intl.formatMessage({ id: 'formPlaceholders.newPassword' })}
                      visibilityToggle={false}
                    />
                    <span className={`${styles.passwordStrength} ${styles[passwordStrength]}`}><FormattedMessage id={`formErrors.password.${passwordStrength}`} /></span>
                  </div>
                )}
              />
              {errors.newPassword?.type === "required" && <div className={styles.errorMessage}>{errors.newPassword.message}</div>}
              {errors.newPassword?.type === "pattern" && <div className={styles.errorMessage}><FormattedMessage id="formErrors.password.pattern" /></div>}
            </div>
            <div className={styles.fieldWrapper}>
              <label className={styles.floatingLabel}>
                {<FormattedMessage id={`userSettings.repeatPassword`} />}
              </label>
              <Controller
                name="repeatPassword"
                control={control}
                rules={{
                  required: intl.formatMessage({ id: 'formErrors.password.required' }),
                  validate: value => value === watchPassword || intl.formatMessage({ id: 'formErrors.password.mismatch' })
                }}
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    className={errors.repeatPassword && 'error'}
                    placeholder={intl.formatMessage({ id: 'formPlaceholders.repeatPassword' })}
                    visibilityToggle={false}
                  />
                )}
              />
              {errors.repeatPassword && <div className={styles.errorMessage}>{errors.repeatPassword.message}</div>}
            </div>
          </div>
        </div>
      </div>
    </Popup>
  }

  return renderPasswordPopup();
}

export default ChangePassword;